import { palette, spaces } from '@kimuno/theme';
import { IconButton, Stack, SvgIcon, TextField, Typography } from '@mui/material';
import { debounce } from 'debounce';
import type React from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { updateGeneratedDocument as updateGeneratedDocumentAction } from '../../../../actions/aiAssistant.actions';
import { muiInputFieldBorderStyle } from '../../../../constants';
import {
	AIAssistantEditorMode,
	aiAssistantEditorContext,
} from '../../../../state/AIAssistantEditorProvider';
import getMuiIcon from '../../../../utils/getMuiIcon';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';
import useAction from '../../../../utils/useAction';
import { TITLE_SAVE_DEBOUNCE_MS } from '../constants';
import usePodcastAudioDownload from './usePodcastAudioDownload';

const ICON_SIZE = 24;

const noHoverEffect = {
	'&:hover': {
		backgroundColor: 'transparent',
	},
};

const getCheckIconButtonStyles = {
	...noHoverEffect,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	padding: 0,
	marginRight: spaces.md,
	minWidth: 'auto',
};

interface CheckIconButtonProps {
	onClick: () => void;
}

const CheckIconButton: React.FC<CheckIconButtonProps> = ({ onClick }) => (
	<IconButton
		data-testid="text-editor-mobile-check-button"
		onClick={onClick}
		// Keep the textfield focused so its blur does not unmount this button mid-tap.
		onMouseDown={(event) => event.preventDefault()}
		onTouchStart={(event) => event.preventDefault()}
		sx={getCheckIconButtonStyles}
	>
		<SvgIcon
			component={getMuiIcon('checkboxCircle')}
			sx={{
				width: ICON_SIZE,
				height: ICON_SIZE,
			}}
		/>
	</IconButton>
);

interface DownloadIconButtonProps {
	onClick: () => void;
	disabled: boolean;
}

const DownloadIconButton: React.FC<DownloadIconButtonProps> = ({ onClick, disabled }) => {
	const { t } = useTranslation();

	return (
		<IconButton
			data-testid="podcast-mobile-download-button"
			aria-label={t('Audio herunterladen')}
			onClick={onClick}
			disabled={disabled}
			color="primary"
			sx={getCheckIconButtonStyles}
		>
			<SvgIcon
				component={getMuiIcon('download')}
				sx={{
					width: ICON_SIZE,
					height: ICON_SIZE,
				}}
			/>
		</IconButton>
	);
};

const AIAssistantTextEditorMobileHeader: React.FC = () => {
	const {
		editorData,
		closeTextEditor,
		hasUnsavedChanges,
		dismissChangeIndicator,
		markTitleChanged,
		mode,
		podcastDetails,
		updatePodcastTitle,
		flushPodcastTitleSave,
	} = useContext(aiAssistantEditorContext);
	const truncateSx = getTextTruncationSx(1);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editedTitle, setEditedTitle] = useState(editorData.title);
	const editInputRef = useRef<HTMLInputElement>(null);
	const updateGeneratedDocument = useAction(updateGeneratedDocumentAction);
	const { handleAudioDownload, isAudioDownloadDisabled } = usePodcastAudioDownload();

	const isPodcast = mode === AIAssistantEditorMode.PODCAST;
	const isEditable = isPodcast ? Boolean(podcastDetails?.id) : Boolean(editorData.id);
	// Podcasts have no content edits, so the check only reflects title editing.
	const showCheckIcon = isPodcast ? isEditMode : hasUnsavedChanges || isEditMode;

	const debouncedUpdate = useMemo(
		() =>
			debounce((newTitle: string) => {
				if (!editorData.id) return;
				updateGeneratedDocument({
					documentId: editorData.id,
					payload: { title: newTitle },
				});
			}, TITLE_SAVE_DEBOUNCE_MS),
		[editorData.id, updateGeneratedDocument]
	);

	const toggleEditMode = () => {
		setIsEditMode((prev) => !prev);

		setTimeout(() => {
			if (editInputRef.current) {
				setEditedTitle(editorData.title);
				editInputRef.current.focus();
			}
		}, 0);
	};

	const handleCheckClick = () => {
		if (isEditMode) {
			setIsEditMode(false);
		}
		if (isPodcast) {
			flushPodcastTitleSave();
		} else {
			dismissChangeIndicator();
		}
	};

	const handleBackClick = () => {
		if (!isPodcast) {
			closeTextEditor();
			return;
		}
		flushPodcastTitleSave();
		closeTextEditor();
	};

	const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newTitle = event.target.value;
		if (isPodcast) {
			setEditedTitle(newTitle);
			updatePodcastTitle(newTitle);
			return;
		}
		if (!editorData.id) return;
		setEditedTitle(newTitle);
		markTitleChanged();
		debouncedUpdate(newTitle);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			setIsEditMode(false);
			if (isPodcast) {
				flushPodcastTitleSave();
			}
		} else if (event.key === 'Escape') {
			event.preventDefault();
			setIsEditMode(false);
		}
	};

	useEffect(() => {
		if (!isEditMode) {
			setEditedTitle(editorData.title);
		}
	}, [editorData.title, isEditMode]);

	useEffect(() => {
		return () => {
			debouncedUpdate.clear?.();
		};
	}, [debouncedUpdate]);

	return (
		<Stack
			data-testid="ai-assistant-text-editor-mobile-header"
			direction="row"
			justifyContent="space-between"
			alignItems="center"
			gap={spaces.xs}
			sx={{
				paddingY: spaces.xs,
				paddingX: 0,
				position: 'sticky',
				top: 0,
				width: '100%',
				backgroundColor: palette.background.paperElevation2,
				borderBottom: `1px solid ${palette.divider}`,
				zIndex: 1,
			}}
		>
			<IconButton
				data-testid="text-editor-mobile-back-button"
				onClick={handleBackClick}
				sx={{
					padding: spaces.xs,
					...noHoverEffect,
				}}
			>
				<SvgIcon
					component={getMuiIcon('arrowLeft')}
					sx={{
						width: ICON_SIZE,
						height: ICON_SIZE,
						fill: palette.text.primary,
					}}
				/>
			</IconButton>
			{isEditMode ? (
				<>
					<TextField
						inputRef={editInputRef}
						fullWidth
						size="small"
						value={editedTitle}
						onChange={handleTitleChange}
						variant="outlined"
						inputProps={{ 'aria-label': 'Title' }}
						onBlur={toggleEditMode}
						onKeyDown={handleKeyDown}
						onFocus={isPodcast ? undefined : markTitleChanged}
						InputProps={{
							sx: {
								...muiInputFieldBorderStyle,
							},
						}}
					/>
					{isEditable && <CheckIconButton onClick={handleCheckClick} />}
				</>
			) : (
				<>
					<Typography
						variant="h6"
						sx={{
							flex: 1,
							minWidth: 0,
							paddingX: spaces.xs,
							...truncateSx,
							...(isEditable && {
								cursor: 'pointer',
							}),
						}}
						onClick={isEditable ? toggleEditMode : undefined}
					>
						{editorData.title}
					</Typography>
					{showCheckIcon && isEditable && <CheckIconButton onClick={handleCheckClick} />}
					{isPodcast && !showCheckIcon && (
						<DownloadIconButton onClick={handleAudioDownload} disabled={isAudioDownloadDisabled} />
					)}
				</>
			)}
		</Stack>
	);
};

export default AIAssistantTextEditorMobileHeader;
